import { RotateCcw } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Chase } from "@/campaigns/modules/chase/types";
import {
  canRestart,
  chasePhase,
  chaseUrgency,
  inReachRung,
  spinBudget,
  spinsUsed,
} from "@/campaigns/modules/chase/types";
import { SectionLabel } from "@/components/campaigns/campaign-shell/campaign-sections";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { PrizeText } from "@/components/prize-text";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAction } from "@/hooks/use-action";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";

/** How long the Restart button waits for the push that zeroes the run before giving up. */
const RESTART_PENDING_MS = 10_000;

/**
 * The "Current step" card: the spins used against the run's budget on the left, the prize the
 * player wins by finishing every target now on the right, Restart in the corner.
 *
 * Restart stays on the card while the operator configured a threshold, disabled until the
 * backend says the player may (`can_reset`) — so it reads as "there is a restart, not yet"
 * rather than appearing out of nowhere — is confirmed inline because it wipes the run, and is
 * pending until the push that zeroes the spins lands (or a timeout, so a lost ack never leaves
 * it stuck). The figure is announced on change; motion is confined to the digit swap and off
 * under `prefers-reduced-motion`. The final few spins before the last rung slips away light the
 * rim in the urgent colour; all-won and finished go quiet.
 */
export const ChaseProgressCard = ({
  chase,
  joined,
  className,
}: {
  chase: Chase;
  /** Restart is the player's lever on THEIR run: nothing to offer before they have joined. */
  joined: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const { restartChase } = useAction();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);

  const phase = chasePhase(chase);
  const used = spinsUsed(chase);
  const budget = spinBudget(chase);
  const rung = inReachRung(chase);
  const chasing = phase === "chasing";
  const urgent = chasing && chaseUrgency(chase) === "urgent";
  const over = phase === "finished" || phase === "allWon";

  const threshold = chase.spinsBeforeRestart;
  const allowed = canRestart(chase);
  // No threshold configured: restarting is not a feature of this chase.
  const restartable = chasing && joined && threshold != null;

  useEffect(() => {
    if (pending && used === 0) setPending(false);
  }, [pending, used]);
  useEffect(() => {
    if (!pending) return;
    const id = setTimeout(() => setPending(false), RESTART_PENDING_MS);
    return () => clearTimeout(id);
  }, [pending]);

  // The number and the prize share one type size and leading (the prize's `hero` ramp), and each
  // sits in a row of that exact height, so their baselines meet across the divider whatever the
  // breakpoint — a flex baseline would not do it: the prize's marquee wrapper carries a
  // zero-height measuring block that throws baseline alignment off.
  const countClass = cn(
    "font-heading text-[40px] leading-[1.12] font-extrabold tabular-nums lg:text-[52px]",
    over ? "text-muted-foreground" : urgent ? "text-urgent" : "text-foreground"
  );
  const figureRow = "flex h-[45px] min-w-0 items-center lg:h-[58px]";

  return (
    <Card
      title={t("chase.currentStep", "Current step")}
      aside={
        restartable && (
          <Button
            variant="secondary"
            size="sm"
            disabled={!allowed || pending}
            aria-busy={pending}
            aria-expanded={confirming}
            onClick={() => setConfirming((open) => !open)}
          >
            <RotateCcw strokeWidth={2.25} />
            {pending
              ? t("chase.restarting", "Restarting…")
              : t("chase.restart", "Restart")}
          </Button>
        )
      }
      urgent={urgent}
      data-phase={phase}
      className={cn("flex flex-col gap-4", className)}
    >
      {/* Two stats side by side, a hairline between them; stacked on narrow phones. Label,
          figure and footnote rows line up column to column. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-0">
        <div className="flex min-w-0 flex-col gap-1.5 sm:pr-4">
          <SectionLabel>{t("chase.spinsUsed", "Spins used")}</SectionLabel>
          <div
            role="heading"
            aria-level={3}
            aria-live="polite"
            className={cn(figureRow, "gap-2")}
          >
            {/* No confident "0" before the first push: a dash says "not known yet". */}
            {used === null ? (
              <span className={countClass}>—</span>
            ) : reduceMotion || over ? (
              <span className={countClass}>{used}</span>
            ) : (
              <AnimatedNumber value={used} className={countClass} />
            )}
            {budget !== null && (
              <span className="self-end pb-[5px] font-heading text-[20px] leading-6 font-semibold text-muted-foreground tabular-nums lg:pb-[7px] lg:text-[24px]">
                / {budget}
              </span>
            )}
          </div>
          {restartable && !allowed && (
            <p className="font-body text-[12px] leading-4 text-muted-foreground">
              {t(
                "chase.restartAfter",
                "You can restart after {{count}} spins",
                {
                  count: threshold,
                }
              )}
            </p>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 sm:border-l sm:border-foreground/[0.06] sm:pl-4">
          <SectionLabel>
            {t("prizes.currentPrize", "Current prize")}
          </SectionLabel>
          {rung && chasing ? (
            <>
              {/* The prize is the card's priority content: it takes the width and glides rather
                  than truncates, its games under it. */}
              <div className={figureRow}>
                <PrizeText
                  size="hero"
                  icon
                  currencyMark={!rung.prize.is_catalog_item}
                  fit="marquee"
                  className="max-w-full min-w-0"
                >
                  {formatPrizeTitle(rung.prize.title, t)}
                </PrizeText>
              </div>
              <PrizeGamesChips games={rung.prize.games ?? []} />
            </>
          ) : (
            <>
              <div className={figureRow}>
                <PrizeText
                  size="hero"
                  tone="muted"
                  className="max-w-full min-w-0"
                >
                  —
                </PrizeText>
              </div>
              {chasing && used !== null && (
                <p className="font-body text-[12px] leading-4 text-muted-foreground">
                  {t(
                    "chase.overBudget",
                    "Out of spins for every prize this run — the next bet starts a new one"
                  )}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {confirming && restartable && allowed && (
        <div className="flex w-full flex-wrap items-center justify-between gap-2 border-t border-foreground/[0.06] pt-3">
          <p className="font-body text-[12px] leading-4 text-foreground-secondary">
            {t(
              "chase.restartConfirm",
              "Restart? Your targets and spins reset."
            )}
          </p>
          <span className="flex items-center gap-1">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setConfirming(false);
                setPending(true);
                restartChase(chase.campaign);
              }}
            >
              <RotateCcw strokeWidth={2.25} />
              {t("chase.restart", "Restart")}
            </Button>
            <Button
              variant="quiet"
              size="sm"
              className="min-h-8"
              onClick={() => setConfirming(false)}
            >
              {t("chase.keepGoing", "Keep going")}
            </Button>
          </span>
        </div>
      )}
    </Card>
  );
};
